import { type FunctionComponent, useState } from 'react';
import { ACCOUNT_LOGIN_LINK, ORDER_STATUS_LINK, SUPPORT_EMAIL_US_LINK, SUPPORT_FAQ_LINK } from '../../../constants/links';
import { useCustomer } from '../../../hooks/apollo/customer/customer.hooks';
import { useSiteViewPreference } from '../../../hooks/apollo/employee/employee.hooks';
import { useContactInfo } from '../../../hooks/apollo/site/site.hooks';
import { StyledButton } from '../../buttons';
import { LinkButton } from '../../buttons/link-button/link-button.component';
import { ExpandablePanel } from '../../common-components/expandable-panel/expandable-panel.component';
import { Link } from '../../common-components/link/link.component';
import { StyledLink } from '../../common-components/link/styled-link.component';
import { PanelComponent } from '../../common-components/panel-component/panel-component.component';
import { OrderLookupForm } from '../../order-lookup-form/order-lookup-form.component';

export type NoSearchResultsProps = { query: string; isNonstock?: boolean; hideQuery?: boolean };

export const NoSearchResults: FunctionComponent<NoSearchResultsProps> = ({ query, isNonstock = false, hideQuery = false }) => {
	return (
		<div data-automation="no-search-results-page">
			<h1 className="font-medium mt0">No results for {hideQuery ? 'your search' : <>"{query}"</>}</h1>
			<EmployeeSearchStatus query={query} isNonstock={isNonstock} />
			<b>Search Help</b>
			<ul>
				<li className="lh-copy">Check your search for typos</li>
				<li className="lh-copy">Use more generic search terms</li>
				<li className="lh-copy">The product you're searching for may be discontinued or not yet on our site</li>
				<NeedHelpItem />
			</ul>
			<div id="dy-recs-search-page-1" />
			<BottomPanels />
		</div>
	);
};

export type NoResultsProps = { type: string };

export const NoResults: FunctionComponent<NoResultsProps> = ({ type }) => {
	return (
		<div className="ml3-l" data-automation={`no-${type}-product-list-results`}>
			<h2>No products found</h2>
			<b>Help</b>
			<ul>
				<li className="lh-copy">
					This {type} has no products. Please try again later, or use the search bar at the top of the page.
				</li>
				<NeedHelpItem />
			</ul>
			<BottomPanels />
		</div>
	);
};

const NeedHelpItem: FunctionComponent = () => {
	const {
		phone: { number: phoneNumber }
	} = useContactInfo();

	return (
		<li className="lh-copy">
			Need help finding something? Call <StyledLink url={`tel:${phoneNumber}`}>{phoneNumber}</StyledLink> or{' '}
			<StyledLink url={SUPPORT_EMAIL_US_LINK}>Email Us</StyledLink>
		</li>
	);
};

const BottomPanels: FunctionComponent = () => {
	const { hasAccount } = useCustomer();
	return (
		<div className="flex flex-column flex-row-ns pt3">
			{hasAccount ? <OrderLookupFormContainer /> : <RedirectButtons />}
			<NeedHelpPanel />
		</div>
	);
};

export const OrderLookupFormContainer: FunctionComponent<{ className?: string; automationHook?: string }> = ({
	className,
	automationHook
}) => {
	const [panelExpanded, setPanelExpanded] = useState(false);
	const toggleExpanded = () => {
		setPanelExpanded(!panelExpanded);
	};

	return (
		<div className={className || 'w-100 w-50-ns pr1'}>
			<ExpandablePanel
				className="f5 br2"
				heading={"Can't Find Your Order?"}
				startExpanded={panelExpanded}
				onExpand={toggleExpanded}
				automationHook={automationHook}
				onCollapse={toggleExpanded}>
				<p className="mt0">Perhaps you purchased as a guest or through a different account. Try entering order info below.</p>
				<OrderLookupForm />
			</ExpandablePanel>
		</div>
	);
};

const RedirectButtons: FunctionComponent = () => (
	<div className="w-100 w-50-ns pr1-ns">
		<PanelComponent className="f5 br2" headingContent={'Looking for Order, Return, or Shipping Status?'}>
			<>
				<p className="mt0">First we need to find your order.</p>
				<div className="flex">
					<Link url={ACCOUNT_LOGIN_LINK}>
						<StyledButton buttonStyle="SECONDARY">Login to Account</StyledButton>
					</Link>
					<Link url={ORDER_STATUS_LINK} className="pl2">
						<StyledButton buttonStyle="SECONDARY">Find Guest Order</StyledButton>
					</Link>
				</div>
			</>
		</PanelComponent>
	</div>
);

const NeedHelpPanel: FunctionComponent = () => (
	<div className="w-100 w-50-ns mt3 mt0-ns pl1-ns">
		<PanelComponent className="f5 br2" headingContent={'Need Help?'}>
			<>
				<p className="mt0">Find return &amp; shipping policies, manufacturer warranty info and frequently asked questions.</p>
				<StyledLink url={SUPPORT_FAQ_LINK}>Visit our Help Center</StyledLink>
			</>
		</PanelComponent>
	</div>
);

const EmployeeSearchStatus: FunctionComponent<NoSearchResultsProps> = ({ query, isNonstock = false }) => {
	const { showAsEmployee } = useSiteViewPreference();
	if (!showAsEmployee) {
		return null;
	}
	return (
		<div className="mb4 w-50-l">
			<div className="pv3 ph4 theme-white bg-theme-internal fw5">Internal Only</div>
			<div className="flex items-center pv3 ph4 bg-theme-internal-lighter ba b--theme-grey-light lh-copy">
				<div className="flex-grow-1">
					0 results in <b>Stock</b>
					{isNonstock && <> + </>}
					{isNonstock && <b>Nonstock</b>}
				</div>
				{!isNonstock && (
					<div>
						<LinkButton buttonStyle="INTERNAL" url={`?term=${encodeURIComponent(query)}&ns=true`}>
							Search Nonstock
						</LinkButton>
					</div>
				)}
			</div>
		</div>
	);
};
